import { GraphQLResult } from 'aws-amplify/api';
import { useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { CaseMap } from '@/features/caseCluster/caseCluster.types';
import { parseCaseMapFromCaseCluster } from '@/features/caseCluster/helpers/createCaseMapAccordingToCaseCluster';
import { useUserAuthenticationContext } from '@/features/userAuth/context/UserAuthenticationContext';
import { CaseStats, CaseStatsByMeetingQuery } from '@/services/API';
import { caseStatsByMeeting } from '@/services/graphql/queries';
import { callGraphQLApi } from '@/utils/graphQLAPI';
import { assertIsTruthy } from '@/utils/types/type-assertion/generic/assertIsTruthy';
import { mutationCreateCaseSlideStats, mutationCreateCaseStats } from '../../graphql/create';

export default function useMeetingCaseStatsSanityCheck(
  meetingID: string | null | undefined,
  caseClusterID: string | null | undefined,
  activated: boolean
) {
  const [didSlideStatsCheck, setDidSlideStatsCheck] = useState(false);
  const { isAdmin } = useUserAuthenticationContext();

  const caseMapQuery: UseQueryResult<CaseMap> = useQuery({
    queryKey: ['caseMap', caseClusterID],
    refetchOnWindowFocus: false,
    enabled: !!caseClusterID && isAdmin && activated,

    queryFn: async () => {
      assertIsTruthy(caseClusterID);
      const caseMap = await parseCaseMapFromCaseCluster({
        caseClusterID: caseClusterID,
        caseCluster: undefined,
      });
      console.log('DEBUG >> sanityCheck, casemap', meetingID, caseClusterID, caseMap);
      return caseMap;
    },
  });

  const caseStatsQuery: UseQueryResult<CaseStats[]> = useQuery({
    queryKey: ['caseStats'],
    refetchOnWindowFocus: false,
    enabled:
      caseMapQuery.status === 'success' &&
      caseMapQuery.fetchStatus === 'idle' &&
      activated &&
      isAdmin,
    queryFn: async () => {
      console.log(
        'DEBUG >> caseStats query based on casemap',
        caseMapQuery.data,
        caseMapQuery.isLoading
      );
      assertIsTruthy(caseMapQuery.data);
      //NOTE: list all caseStats for this meeting
      const caseStatsCall = await callGraphQLApi<GraphQLResult<CaseStatsByMeetingQuery>>(
        caseStatsByMeeting,
        {
          meetingID: meetingID,
        }
      );
      const caseStats = caseStatsCall.data?.caseStatsByMeeting?.items || [];
      // NOTE: check if they are all there
      const allCaseStats = await Promise.all(
        Object.keys(caseMapQuery.data).map(async (key) => {
          const patientCase = caseMapQuery.data[key];
          const foundItem = caseStats.find((el) => el?.caseId === patientCase.id);
          // NOTE: if one is missing, create it
          if (!foundItem) {
            const res = await mutationCreateCaseStats(meetingID || '', patientCase.id, patientCase);
            return res.data?.createCaseStats as CaseStats;
          }
          return Promise.resolve(foundItem);
        })
      );
      return allCaseStats;
    },
  });

  useEffect(() => {
    async function checkCaseStats() {
      if (
        !isAdmin ||
        caseMapQuery.status !== 'success' ||
        caseMapQuery.fetchStatus !== 'idle' ||
        caseStatsQuery.status !== 'success' ||
        caseStatsQuery.fetchStatus !== 'idle' ||
        didSlideStatsCheck ||
        !activated
      )
        return;
      console.log('DEBUG >> started sanity check of stats');

      // NOTE: For each CaseStats, load the patientCase data
      // const patientCaseData = await Promise.all(
      //   caseStatsQuery.data.map(async (elem) => {
      //     const res = await fetchCaseData(
      //       [elem.caseId],
      //       [caseList.find((el) => el.id === elem.caseId)!.path]
      //     );
      //     return res[0];
      //   })
      // );

      // NOTE: For each patientCase data, check if all slides have an associated caseSlideStats
      const promises = Object.keys(caseMapQuery.data).map(async (key) => {
        const patientCase = caseMapQuery.data[key];
        const caseStatsElem = caseStatsQuery.data.find((el) => el.caseId === patientCase.id);
        await Promise.all(
          patientCase.slides.map((slide) => {
            const foundMatchingSlideStats = !!caseStatsElem?.slides?.items.find(
              (el) => el?.slideId === slide.id
            );
            if (!foundMatchingSlideStats) {
              console.log('DEBUG >> have to fix some stats');
              return mutationCreateCaseSlideStats(caseStatsElem?.id || '', slide.id, 0);
            }
          })
        );
      });

      await Promise.all(promises);
      setDidSlideStatsCheck(true);
      console.log('DEBUG >> finished sanity check of stats');
    }
    checkCaseStats();
  }, [isAdmin, didSlideStatsCheck, caseStatsQuery, caseMapQuery, activated]);

  return {
    isLoading:
      isAdmin &&
      !didSlideStatsCheck &&
      !caseStatsQuery.isLoading &&
      !caseMapQuery.isLoading &&
      activated,
  };
}
